import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Video, Plus, Calendar, Users, ArrowRight, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { BACKEND_URL } from '@/config/api';

export default function MeetingsPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isJoinOpen, setIsJoinOpen] = useState(false);
  const [meetingName, setMeetingName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [copied, setCopied] = useState(null);

  // Create instant meeting
  const createMeeting = async () => {
    if (!meetingName.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/meetings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: meetingName,
          is_instant: true
        })
      });
      
      if (response.ok) {
        const meeting = await response.json();
        navigate(`/meeting/${meeting.id}`);
      }
    } catch (error) {
      console.error('Error creating meeting:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Join meeting by code
  const joinMeeting = async () => {
    if (!joinCode.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/meetings/code/${joinCode}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const meeting = await response.json();
        
        // Join the meeting
        await fetch(`${BACKEND_URL}/api/meetings/${meeting.id}/join`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            display_name: user.name,
            audio_enabled: true,
            video_enabled: true
          })
        });
        
        navigate(`/meeting/${meeting.id}`);
      } else {
        alert('Meeting not found. Please check the code.');
      }
    } catch (error) {
      console.error('Error joining meeting:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 overflow-x-hidden" data-testid="meetings-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4">
        <div className="max-w-6xl mx-auto flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Meetings</h1>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mt-1">Create or join video meetings with your team</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => setIsJoinOpen(true)}
              className="dark:border-gray-700 dark:text-gray-300"
              data-testid="join-meeting-btn"
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              Join Meeting
            </Button>
            <Button
              onClick={() => setIsCreateOpen(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-meeting-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Meeting
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <button
            onClick={() => setIsCreateOpen(true)}
            className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl text-white text-left hover:from-blue-600 hover:to-blue-700 transition-all group"
          >
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Video size={24} />
            </div>
            <h3 className="text-lg font-semibold mb-1">Start Instant Meeting</h3>
            <p className="text-blue-100 text-sm">Create a meeting and invite others to join</p>
          </button>
          
          <button
            onClick={() => setIsJoinOpen(true)}
            className="p-6 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 text-left hover:border-blue-500 dark:hover:border-blue-500 transition-all group"
          >
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Users size={24} className="text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Join a Meeting</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Enter a meeting code to join</p>
          </button>
          
          <button
            className="p-6 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 text-left hover:border-blue-500 dark:hover:border-blue-500 transition-all group opacity-60 cursor-not-allowed"
            disabled
          >
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-4">
              <Calendar size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Schedule Meeting</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Coming soon...</p>
          </button>
        </div>

        {/* Recent Meetings */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Meetings</h2>
          
          {recentMeetings.length === 0 ? (
            <div className="text-center py-12">
              <Video size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No meetings yet</p>
              <p className="text-gray-400 dark:text-gray-500 text-sm">Start a new meeting to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentMeetings.map(meeting => (
                <div
                  key={meeting.id}
                  className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 dark:bg-slate-700 rounded-lg"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Video size={20} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">{meeting.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{meeting.created_at}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => copyCode(meeting.room_code)}
                      className="px-3 py-1 text-sm bg-gray-200 dark:bg-slate-600 rounded flex items-center gap-1"
                    >
                      {copied === meeting.room_code ? <Check size={14} /> : <Copy size={14} />}
                      {meeting.room_code}
                    </button>
                    <Button 
                      size="sm"
                      onClick={() => navigate(`/meeting/${meeting.id}`)}
                    >
                      Rejoin
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Meeting Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Create New Meeting</DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-gray-400">
              Start an instant video meeting with your team
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Meeting Name
            </label>
            <Input
              value={meetingName}
              onChange={(e) => setMeetingName(e.target.value)}
              placeholder="e.g., Weekly Standup"
              className="dark:bg-slate-700 dark:border-gray-600"
              data-testid="meeting-name-input"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateOpen(false)}
              className="dark:border-gray-600 dark:text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={createMeeting}
              disabled={!meetingName.trim() || isLoading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="start-meeting-submit"
            >
              {isLoading ? 'Creating...' : 'Start Meeting'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Join Meeting Dialog */}
      <Dialog open={isJoinOpen} onOpenChange={setIsJoinOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Join Meeting</DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-gray-400">
              Enter the meeting code to join
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Meeting Code
            </label>
            <Input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="e.g., ABC12345"
              className="dark:bg-slate-700 dark:border-gray-600 uppercase"
              data-testid="join-code-input"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsJoinOpen(false)}
              className="dark:border-gray-600 dark:text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={joinMeeting}
              disabled={!joinCode.trim() || isLoading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="join-meeting-submit"
            >
              {isLoading ? 'Joining...' : 'Join Meeting'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
